import React from 'react';
import styled from 'styled-components';
import { FaShieldAlt, FaMobileAlt, FaUsers, FaComment, FaRobot, FaTools } from 'react-icons/fa';
import { 
  chatwithagentScreen, 
  agentGroupChatScreen, 
  chatScreen, 
  agentStoreScreen, 
  aiModelsDownloadScreen, 
  createCustomAgentScreen1 
} from '../assets';

const ShowcaseSection = styled.section`
  padding: 6rem 0;
  background-color: #f8fafc;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -100px;
    right: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(var(--primary-rgb), 0.1), rgba(var(--secondary-rgb), 0.1));
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    left: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(var(--secondary-rgb), 0.1), rgba(var(--primary-rgb), 0.1));
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ShowcaseItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
  }
`;

const ShowcaseImageContainer = styled.div`
  width: 100%;
  max-width: 280px;
  margin-bottom: 1.5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 15px;
    left: 15px;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    border-radius: 20px;
    z-index: -1;
    opacity: 0.5;
  }
  
  @media (max-width: 992px) {
    max-width: 260px;
  }
  
  @media (max-width: 576px) {
    max-width: 280px;
  }
`;

const ShowcaseImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 3px solid white;
`;

const ShowcaseContent = styled.div`
  text-align: center;
  max-width: 90%;
`;

const ShowcaseTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--text-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: 0.75rem;
    color: var(--primary-color);
  }
`;

const ShowcaseDescription = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const ProductShowcase = () => {
  const showcaseItems = [
    {
      icon: <FaShieldAlt />,
      title: "Enhanced Privacy",
      description: "All conversations secured with end-to-end encryption. Your data stays on your device with local AI processing.",
      image: chatwithagentScreen
    },
    {
      icon: <FaUsers />,
      title: "Multi-Agent Groups",
      description: "Bring multiple AI assistants into group conversations to facilitate discussions and enhance collaboration.",
      image: agentGroupChatScreen
    },
    {
      icon: <FaMobileAlt />,
      title: "Mobile Experience",
      description: "Our lightweight app delivers advanced AI in a familiar messaging interface for productivity on the go.",
      image: chatScreen
    },
    {
      icon: <FaComment />,
      title: "AI Agents Library",
      description: "Browse our growing collection of specialized AI agents designed for different tasks and use cases.",
      image: agentStoreScreen
    },
    {
      icon: <FaRobot />,
      title: "Custom AI Models",
      description: "Choose from different AI model types based on your specific needs and device capabilities.",
      image: aiModelsDownloadScreen
    },
    {
      icon: <FaTools />,
      title: "Agent Customization",
      description: "Create your own specialized AI assistants tailored to your specific needs and preferences.",
      image: createCustomAgentScreen1
    }
  ];

  return (
    <ShowcaseSection id="showcase">
      <Container>
        <SectionHeader>
          <SectionTitle>Product <span>Highlights</span></SectionTitle>
          <SectionSubtitle>
            Explore the key features that make our hallow AI platform a powerful tool for 
            enhanced communication and productivity.
          </SectionSubtitle>
        </SectionHeader>
        
        <ShowcaseGrid>
          {showcaseItems.map((item, index) => (
            <ShowcaseItem key={index}>
              <ShowcaseImageContainer>
                <ShowcaseImage src={item.image} alt={item.title} />
              </ShowcaseImageContainer>
              <ShowcaseContent>
                <ShowcaseTitle>{item.icon} {item.title}</ShowcaseTitle>
                <ShowcaseDescription>{item.description}</ShowcaseDescription>
              </ShowcaseContent>
            </ShowcaseItem>
          ))}
        </ShowcaseGrid>
      </Container>
    </ShowcaseSection>
  );
};

export default ProductShowcase; 